import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Combobox, FormField, Layout } from '@vwfs-bronson/bronson-react';
import { Contract } from 'cpes-common';
import { LicensePlate } from '../../../../license-plate';
import { getProductCategoryTranslationLabel } from '../../../../contracts/ProductCategoryTranslationLabel';
import { ComboboxState } from '../../../../../types/bronson-react';
import { ComboboxOption } from '@cp-shared-19/frontend-ui';

export type ContractDropdownProps = {
    label: string;
    contracts: Contract[];
    name: string;
    onChange: (optionKey: string) => void;
};

type OptionItem = {
    content: JSX.Element;
    optionKey: string;
    optionValue: string;
    testId: string;
};

export const ContractDropdown: React.FC<ContractDropdownProps> = ({ label, contracts, name, onChange }) => {
    const { t } = useTranslation('my-request');
    const [selectedItem, setSelectedItem] = useState<{ value: ComboboxOption[] }>({ value: [] });

    const dropdownOptions: OptionItem[] = contracts.map(contract => ({
        content: (
            <Layout flush>
                {contract.licensePlateNumber && (
                    <Layout.Item default="1/1">
                        <LicensePlate size={'xsmall'} licensePlateNumber={contract.licensePlateNumber} />
                    </Layout.Item>
                )}
                <Layout.Item default="1/1">
                    <strong>{contract.contractNumber}</strong>
                </Layout.Item>
                <Layout.Item default="1/1">
                    <strong>{t(getProductCategoryTranslationLabel(contract.contractType))}</strong>
                </Layout.Item>
                <Layout.Item default="1/1">{contract.carInformation}</Layout.Item>
            </Layout>
        ),
        optionKey: contract.contractNumber,
        optionValue: contract.contractNumber,
        testId: contract.contractNumber,
    }));

    const generalRequest = t('form.general-request');
    const preselectedOptionKey = '0';
    const generalRequestItem: OptionItem = {
        content: <strong>{generalRequest}</strong>,
        optionKey: preselectedOptionKey,
        optionValue: generalRequest,
        testId: 'generalRequestTestId',
    };

    dropdownOptions.splice(0, 0, generalRequestItem);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onChangeInternal = (selectedOption: any): void => {
        const key = selectedOption[0].key;
        const value = key === preselectedOptionKey ? '' : key;
        setSelectedItem({ value: [selectedOption[0]] });
        onChange(value);
    };

    const defaultOptionKey: ComboboxState = { value: [{ key: preselectedOptionKey, value: generalRequest }] };

    return (
        <FormField className={'iban-input c-form-field--no-floating'} labelText={label} type="select">
            <Combobox
                className={'iban-input c-form-field--no-floating'}
                onChange={onChangeInternal}
                name={name}
                defaultState={defaultOptionKey}
                value={selectedItem.value}
                options={dropdownOptions.map(option => ({
                    key: option.optionKey,
                    value: {
                        textValue: option.optionValue,
                        content: option.content,
                    },
                }))}
                testId={name}
            />
        </FormField>
    );
};
